// Initial vertical position for the first toast in pixels
const TOAST_VERTICAL_OFFSET_START = 80;
// Increment of vertical position between toasts in pixels
const TOAST_OFFSET_INCREMENT = 15;
// Default duration for the toast to be visible in milliseconds
const DEFAULT_DURATION = 3000;
// Maximum value for the progress bar (100%)
const PROGRESS_MAX = 100;

// Define the Toast type with JSDoc
/**
 * @typedef {Object} Toast
 * @property {HTMLElement} element
 * @property {number} [animationFrameId]
 * @property {number} [elapsed]
 * @property {function(): void} [pause]
 * @property {function(): void} [resume]
 * @property {function(): void} [closeToastHandler]
 * @property {boolean} [paused]
 * @property {number} [startTime]
 */

// Set of active toasts
/** @type {Set<Toast>} */
const activeToasts = new Set();

window.showToastsFromSession = showToastsFromSession;

/**
 * Displays a toast message.
 * @param {string} message - The message to be displayed.
 * @param {string} [type='success'] - The type of the toast.
 * @param {number} [duration=DEFAULT_DURATION] - Duration [ms] for the toast to be visible.
 */
export function showToast(message, type = 'success', duration = DEFAULT_DURATION) {
    const existingToast = findExistingToast(message, type);
    if (existingToast) {
        handleExistingToast(existingToast, duration);
    } else {
        createAndShowToast(message, type, duration);
    }
}

/**
 * Creates a toast element with specified message and type using the template.
 * @param {string} message - The message to be displayed in the toast.
 * @param {string} type - The type of the toast (e.g., 'success', 'error').
 * @returns {HTMLElement} The toast container element.
 */
function createToastElement(message, type) {
    const template = document.getElementById('toast-template');
    if (!template) {
        throw new Error("Template element with ID 'toast-template' not found");
    }

    // Clone the template content
    const clone = template.content.cloneNode(true);
    const toastContainer = clone.querySelector('.toast');
    if (!toastContainer) {
        throw new Error("Toast container element not found in the template");
    }

    // Set the type class and initial vertical offset
    toastContainer.classList.add(type);

    // Set ARIA attributes for accessibility
    toastContainer.setAttribute('role', 'alert');
    toastContainer.setAttribute('aria-live', 'assertive');

    // Set the message
    const toastBody = toastContainer.querySelector('.toast-body');
    toastBody.textContent = message;

    return toastContainer;
}

/**
 * Finds an existing toast with the same message and type.
 * @param {string} message - The message of the toast.
 * @param {string} type - The type of the toast.
 * @returns {Toast | undefined} - The existing toast object, if found.
 */
function findExistingToast(message, type) {
    return [...activeToasts].find(toast => toast.element.textContent === message && toast.element.classList.contains(type));
}

/**
 * Handles an existing toast by resetting its duration and highlighting it.
 * @param {Toast} existingToast - The existing toast object.
 * @param {number} duration - The duration to reset the toast to.
 */
function handleExistingToast(existingToast, duration) {
    handleToastInteraction(existingToast, duration, true);
    highlightToast(existingToast.element);
}

/**
 * Creates a new toast element and shows it.
 * @param {string} message - The message of the toast.
 * @param {string} type - The type of the toast.
 * @param {number} duration - The duration for the toast to be visible.
 */
function createAndShowToast(message, type, duration) {
    const toastContainer = createToastElement(message, type);
    if (!document.body) {
        console.error('Document body is not available to append toast element.');
        return;
    }

    /** @type {Toast} */
    const toast = { element: toastContainer, elapsed: 0 };
    document.body.appendChild(toastContainer);
    activeToasts.add(toast);
    updateToastPositions();

    handleToastInteraction(toast, duration);
}

/**
 * Handles user interaction with the toast.
 * @param {Toast} toastContainer - The existing toast object.
 * @param {number} duration - Duration for the toast to be visible.
 * @param {boolean} [reset=false] - Whether to reset the toast timer.
 */
function handleToastInteraction(toastContainer, duration, reset = false) {
    const progressBar = toastContainer.element.querySelector('.progress-bar');

    if (reset && toastContainer.animationFrameId) {
        cancelAnimationFrame(toastContainer.animationFrameId);
    }

    let startTime = performance.now();
    let elapsed = reset ? 0 : toastContainer.elapsed || 0;
    toastContainer.paused = false;

    // Assign custom properties and methods to the toast container
    toastContainer.pause = () => pauseToast(toastContainer);
    toastContainer.resume = () => resumeToast(toastContainer, duration);
    toastContainer.closeToastHandler = () => closeToastHandler(toastContainer);

    // Add event listeners for user interaction
    addEventListeners(toastContainer.element, toastContainer.pause, toastContainer.resume, toastContainer.closeToastHandler);

    // Start the progress animation
    requestAnimationFrame((time) => updateProgress(time, toastContainer, elapsed, duration, startTime, progressBar));
}

/**
 * Pauses the progress animation.
 * @param {Toast} toastContainer - The toast container element.
 */
function pauseToast(toastContainer) {
    toastContainer.paused = true;
    if (toastContainer.animationFrameId) {
        cancelAnimationFrame(toastContainer.animationFrameId);
    }
}

/**
 * Resumes the progress animation.
 * @param {Toast} toastContainer - The toast container element.
 * @param {number} duration - The total duration for the toast to be visible.
 */
function resumeToast(toastContainer, duration) {
    if (toastContainer.elapsed >= duration) return;
    toastContainer.paused = false;
    const remainingTime = duration - toastContainer.elapsed;
    requestAnimationFrame((time) => updateProgress(time, toastContainer, toastContainer.elapsed, remainingTime, performance.now(), toastContainer.element.querySelector('.progress-bar')));
}

/**
 * Closes the toast and cleans up event listeners.
 * @param {Toast} toastContainer - The toast container element.
 */
function closeToastHandler(toastContainer) {
    closeToast(toastContainer.element);
    removeEventListeners(toastContainer);
}

/**
 * Updates the progress bar width based on elapsed time.
 * @param {DOMHighResTimeStamp} time - The current time.
 * @param {Toast} toastContainer - The toast container element.
 * @param {number} elapsed - The elapsed time so far.
 * @param {number} duration - The total duration for the toast to be visible.
 * @param {number} startTime - The start time of the animation.
 * @param {HTMLElement} progressBar - The progress bar element.
 */
function updateProgress(time, toastContainer, elapsed, duration, startTime, progressBar) {
    if (!toastContainer.paused) {
        elapsed += time - startTime;
        startTime = time;
        toastContainer.elapsed = elapsed;
        const width = Math.max(0, PROGRESS_MAX - (PROGRESS_MAX * elapsed) / duration);
        progressBar.style.width = `${width}%`;

        if (elapsed < duration) {
            toastContainer.animationFrameId = requestAnimationFrame((time) => updateProgress(time, toastContainer, elapsed, duration, startTime, progressBar));
        } else {
            closeToast(toastContainer.element);
        }
    }
}

/**
 * Adds event listeners to the toast container for interaction.
 * @param {HTMLElement} toastElement - The toast container element.
 * @param {function(): void} pause - Function to pause the progress animation.
 * @param {function(): void} resume - Function to resume the progress animation.
 * @param {function(): void} closeToastHandler - Function to handle toast close action.
 */
function addEventListeners(toastElement, pause, resume, closeToastHandler) {
    toastElement.querySelector('.toast-close').addEventListener('click', closeToastHandler);
    toastElement.addEventListener('mouseover', () => {
        pause();
    });
    toastElement.addEventListener('mouseout', () => {
        resume();
    });
}

/**
 * Removes event listeners from the specified toast container.
 * This function is used to clean up event listeners when a toast is closed.
 * @param {Toast} toast - The toast object from which to remove event listeners.
 */
function removeEventListeners(toast) {
    if (toast.pause) {
        toast.element.removeEventListener('mouseover', toast.pause);
    }
    if (toast.resume) {
        toast.element.removeEventListener('mouseout', toast.resume);
    }
    const closeButton = toast.element.querySelector('.toast-close');
    if (closeButton && toast.closeToastHandler) {
        closeButton.removeEventListener('click', toast.closeToastHandler);
    }
}

/**
 * Closes the specified toast and removes it from the DOM.
 * @param {HTMLElement} toastElement - The toast container element to be closed.
 */
function closeToast(toastElement) {
    toastElement.classList.add('fade-out');
    setTimeout(() => {
        toastElement.remove();
        const toastToRemove = [...activeToasts].find(toast => toast.element === toastElement);
        if (toastToRemove) {
            activeToasts.delete(toastToRemove);
            removeEventListeners(toastToRemove);
        }
        updateToastPositions();
    }, 500);
}

/**
 * Updates the vertical positions of all active toasts.
 */
function updateToastPositions() {
    let newOffset = TOAST_VERTICAL_OFFSET_START;
    activeToasts.forEach(toastObject => {
        const toastElement = toastObject.element;
        if (toastElement && toastElement.parentNode === document.body) {
            toastElement.style.top = `${newOffset}px`;
            newOffset += toastElement.offsetHeight + TOAST_OFFSET_INCREMENT;
        }
    });
}

/**
 * Highlights the specified toast container to indicate it has been reset.
 * @param {HTMLElement} toastElement - The toast container element to highlight.
 */
function highlightToast(toastElement) {
    toastElement.classList.remove('highlight');
    setTimeout(() => {
        toastElement.classList.add('highlight');
    }, 10);
}

/**
 * Displays toasts from session data.
 * @param {Array<{message: string, type: string}>} sessionToasts - Array of toast objects to be displayed.
 */
function showToastsFromSession(sessionToasts) {
    sessionToasts.forEach(toast => {
        showToast(toast.message, toast.type, 3000);
    });
}
